"use client";

import { withNext } from "@/lib/auth-redirects";
import Link from "next/link";
import { useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Suspense } from "react";
import { login } from "@/app/actions/auth";

function LoginForm() {
  const searchParams = useSearchParams();
  const next = searchParams.get("next");
  const notice =
    searchParams.get("created") === "1"
      ? "Your account is ready. Please log in to continue."
      : searchParams.get("reset") === "1"
        ? "Your password has been updated. Please log in with your new password."
        : searchParams.get("error") === "auth"
          ? "That sign-in link is invalid or has expired. Please log in below."
          : "";
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);

  async function handleSubmit(formData: FormData) {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError("");
    setLoading(true);
    try {
      if (next) formData.set("next", next);
      const result = await login(formData);
      if (result?.error) {
        setError(result.error);
        setLoading(false);
        submittingRef.current = false;
      } else if (result?.redirect) {
        window.location.href = result.redirect;
      }
    } catch (err) {
      console.error("[login] submit failed:", err);
      setError("Something went wrong. Please check your connection and try again.");
      setLoading(false);
      submittingRef.current = false;
    }
  }

  return (
    <div className="bg-warm-bg min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-charcoal mb-2">
            Welcome Back
          </h1>
          <p className="text-text-muted text-sm">
            Log in to your Tradesfolk account.
          </p>
        </div>

        <div className="bg-white rounded-md p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-stone/20">
          {notice && !error && (
            <div className="mb-4 p-3 bg-sage-pale border border-sage-light/50 rounded-sm text-sm text-forest">
              {notice}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-sm text-sm text-error">
              {error}
            </div>
          )}

          <form action={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-body mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-cream border border-stone rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-text-body">
                  Password
                </label>
                <Link
                  href="/forgot-password"
                  className="text-xs text-forest hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="w-full px-4 py-3 bg-cream border border-stone rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm mt-2 disabled:opacity-50"
            >
              {loading ? "Logging in..." : "Log In"}
            </button>
          </form>
        </div>

        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-text-muted">
            Don&apos;t have an account?{" "}
            <Link href={withNext("/signup", next)} className="text-forest font-medium hover:underline">
              Sign up
            </Link>
          </p>
          <div className="flex justify-center gap-4 text-xs text-text-muted">
            <Link href="/signup/tradesperson" className="hover:text-forest transition-colors">
              I&apos;m a tradesperson
            </Link>
            <span>&middot;</span>
            <Link href={withNext("/signup/homeowner", next)} className="hover:text-copper transition-colors">
              I need a tradesperson
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
